import TablePagination from "../pagination/TablePagination";
import { useState, useEffect, useRef, useCallback } from "react";
import { Table, Button, Form, InputGroup } from "react-bootstrap";
import StudentTableRow from "./StudentTableRow";
import { useGetStudentsByTeacher } from "../../../../hooks/teacher/students/useGetStudentsByTeacher";
import TableError from "../../common/TableError";
import { ITEMS_PER_PAGE, SEARCH_DEBOUNCE_TIME } from "../../../../constant/constant";
import TableSkeleton from "../../common/TableSkeleton"
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import { useToggleStudentStatus } from "../../../../hooks/school/student/useToggleStudentStatus";


const StudentTable = () => {
  const { loading, error, students, meta, getStudentsByTeacher } = useGetStudentsByTeacher();
  const {
    toggleStudentStatus,
    loading: toggleLoading,
    error: toggleError,
    success: toggleSuccess,
    resetState: resetToggleState,
  } = useToggleStudentStatus();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState("");

  /**
   * Build the filters object for the API call.
   * When a search term is provided, pass it as a single 'search' filter
   */
  const buildFilters = useCallback((search, status) => {
    const filters = {};
    if (search && search.trim() !== "") {
      filters.search = search.trim();
    }
    if (status !== "") {
      filters.status = status;
    }
    return filters;
  }, []);

  /**
   * Fetch students with current page and search filters
   */
  const fetchStudents = useCallback(
    (page, search, status) => {
      getStudentsByTeacher({
        page,
        per_page: ITEMS_PER_PAGE,
        filters: buildFilters(search, status),
      });
    },
    [buildFilters, getStudentsByTeacher],
  );

  // Fetch students on initial load and when page or debounced search changes
  useEffect(() => {
    fetchStudents(currentPage, debouncedSearch, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, statusFilter]);

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_TIME);
  };

  /**
   * Clear search input and reset results
   */
  const handleClearSearch = () => {
    setSearchTerm("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setCurrentPage(1);
    setDebouncedSearch("");
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    fetchStudents(currentPage, debouncedSearch, statusFilter);
  };

  /**
   * Refresh after delete - handles the edge case when deleting
   * the last item on a page by going back to the previous page
   */
  const handleRefreshAfterDelete = () => {
    if (students.length === 1 && currentPage > 1) {
      // Go back to previous page (useEffect will trigger the fetch)
      setCurrentPage(currentPage - 1);
    } else {
      // Stay on current page and refresh
      fetchStudents(currentPage, debouncedSearch, statusFilter);
    }
  };

  /**
   * Handle status filter change
   */
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  // Show skeleton only on initial load (when students is undefined and loading)
  if (loading && students === undefined) {
    return <TableSkeleton rows={ITEMS_PER_PAGE} columns={4} />;
  }

  if (error || students === null) {
    return <TableError error={error} handleRefresh={handleRefresh} loading={loading} />;
  }

  const totalPages = meta?.last_page || 1;
  const hasStudents = students && students.length > 0;
  const isSearching = debouncedSearch.trim() !== "";

  return (
    <div>
      {/* Search and Refresh Row */}
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div className="d-flex gap-2 flex-wrap flex-grow-1">
          <InputGroup style={{ maxWidth: "400px" }}>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name or email or student id..."
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading && !isSearching && students === undefined}
              className="shadow-none"
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={handleClearSearch}
                disabled={loading}
                title="Clear search"
              >
                <i className="bi bi-x-lg"></i>
              </Button>
            )}
          </InputGroup>

          <Form.Select
            value={statusFilter}
            onChange={handleStatusChange}
            disabled={loading && students === undefined}
            style={{ maxWidth: "200px" }}
            className="shadow-none"
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </Form.Select>
        </div>

        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {!hasStudents ? (
        <div className="alert alert-info mt-3" role="alert">
          {isSearching
            ? `No students found matching "${debouncedSearch}".`
            : "No students found."}
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => {
                  return <StudentTableRow key={student.id} student={student}
                    refetch={handleRefreshAfterDelete} disabled={loading}
                    onToggle={toggleStudentStatus}
                    toggleLoading={toggleLoading}
                    toggleError={toggleError}
                    toggleSuccess={toggleSuccess}
                    resetToggleState={resetToggleState}
                    onToggleSuccess={handleRefresh}
                  />
                })}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default StudentTable;